import { useCopyToClipboard } from '../hooks/useCopyToClipboard'
import { formatINR, calcSavings } from '../utils/formatters'
import PlatformBadge from './PlatformBadge'
import DiscountBadge from './DiscountBadge'

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
)

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
)

/**
 * Individual deal card. Handles its own copy-link state.
 * @param {{ deal: import('../data/deals').Deal, index: number }} props
 */
const DealCard = ({ deal, index = 0 }) => {
  const { copied, copy } = useCopyToClipboard()
  const savings = calcSavings(deal.originalPrice, deal.discountedPrice)

  const onClickCard = (e) => {
    window.open(deal.affiliateUrl, "_blank", "noopener,noreferrer");
    e.stopPropagation();
  };

  return (
    <article
      className="deal-card group relative bg-white rounded-2xl overflow-hidden flex flex-col
        shadow-card hover:shadow-card-hover
        border border-slate-100 hover:border-brand-200
        transition-all duration-300 hover:-translate-y-1"
      style={{ animationDelay: `${index * 60}ms` }}
      onClick={(e) => onClickCard(e)}
    >
      {/* ── Image ── */}
      <div className="relative overflow-hidden bg-slate-100 h-48 shrink-0">
        <DiscountBadge percent={Math.round(((deal.originalPrice - deal.discountedPrice)/deal.originalPrice)*100)} />

        {deal.trending && (
          <div className="absolute top-3 right-3 z-10
            bg-gradient-to-r from-violet-600 to-purple-600
            text-white text-xs font-bold px-2.5 py-0.5 rounded-full shadow">
            🔥 HOT
          </div>
        )}

        <img
          src={deal.image}
          alt={deal.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* ── Body ── */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Meta row */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <PlatformBadge platform={deal.platform} />
          <span className="text-xs text-slate-400 font-medium">{deal.category}</span>
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-slate-800 leading-snug line-clamp-2
          group-hover:text-brand-700 transition-colors duration-200 cursor-pointer">
          {deal.title}
        </h3>

        {/* Pricing */}
        <div className="flex items-end gap-2 mt-auto pt-1">
          <span className="text-2xl font-display font-extrabold text-slate-900 leading-none">
            {formatINR(deal.discountedPrice)}
          </span>
          <div className="flex flex-col mb-0.5 gap-0.5">
            <span className="text-xs text-slate-400 line-through leading-none">
              {formatINR(deal.originalPrice)}
            </span>
            <span className="text-xs font-semibold text-emerald-600 leading-none">
              Save {formatINR(savings)}
            </span>
          </div>
        </div>

        {/* CTA Row */}
        <div className="flex gap-2 pt-1">
          <a
            onClick={(e) => onClickCard(e)}
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-1.5
              bg-gradient-to-r from-brand-600 to-violet-600
              hover:from-brand-700 hover:to-violet-700
              text-white text-sm font-bold
              py-2.5 px-4 rounded-xl
              shadow-md shadow-brand-100 hover:shadow-lg hover:shadow-brand-200
              transition-all duration-300 active:scale-95 cursor-pointer"
            aria-label={`Get deal for ${deal.title}`}
          >
            Get Deal
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>

          <button
            onClick={() => copy(deal.affiliateUrl)}
            title="Copy deal link"
            aria-label="Copy deal link"
            className={`w-10 h-10 flex items-center justify-center rounded-xl border
              transition-all duration-200 active:scale-95 shrink-0
              ${copied
                ? 'bg-emerald-50 border-emerald-300 text-emerald-600'
                : 'bg-white border-slate-200 text-slate-400 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600'
              }`}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        </div>
      </div>
    </article>
  )
}

export default DealCard
